import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import AuthShell from '../components/auth/AuthShell';
import { useToast } from '../context/ToastContext';

const inputClass =
  'w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-all duration-200 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white';

const SignUp = () => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const toast = useToast();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, password, confirmPassword } = formData;

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Registration failed');
      toast.success(data.message || 'Registration successful! Please check your email to verify.');
      setFormData({ name: '', email: '', password: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create Account"
      subtitle="Register as an employee to access the portal."
      footer={
        <p className="text-center text-sm text-gray-600 dark:text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400">
            Login
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {[
          { name: 'name', type: 'text', icon: FaUser, placeholder: 'Full Name' },
          { name: 'email', type: 'email', icon: FaEnvelope, placeholder: 'Email Address' },
        ].map(({ name, type, icon: Icon, placeholder }) => (
          <div key={name} className="relative group">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <Icon className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500" />
            </div>
            <input
              type={type}
              name={name}
              className={inputClass}
              placeholder={placeholder}
              value={formData[name]}
              onChange={handleInputChange}
              required
            />
          </div>
        ))}

        {[
          { key: 'password', show: showPassword, setShow: setShowPassword, placeholder: 'Password' },
          { key: 'confirmPassword', show: showConfirmPassword, setShow: setShowConfirmPassword, placeholder: 'Confirm Password' },
        ].map(({ key, show, setShow, placeholder }) => (
          <div key={key} className="relative group">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <FaLock className="h-4 w-4 text-gray-400 group-focus-within:text-primary-500" />
            </div>
            <input
              type={show ? 'text' : 'password'}
              name={key}
              className={`${inputClass} pr-11`}
              placeholder={placeholder}
              value={formData[key]}
              onChange={handleInputChange}
              required
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-primary-500"
            >
              {show ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
            </button>
          </div>
        ))}

        <button type="submit" disabled={loading} className="btn-premium w-full py-3.5">
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
    </AuthShell>
  );
};

export default SignUp;
